// CommunicationsList — componente reusável de lista de comunicações.
// Usado em /communications (geral) e tab "Comunicações" do client (filtrado).

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Inbox, Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

import {
  listCommunications,
  type CommunicationItem,
  type CommunicationsFilters,
} from '../../lib/communications-api';

const STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  sent: 'Enviada',
  delivered: 'Entregue',
  failed: 'Falhou',
  received: 'Recebida',
  bounced: 'Devolvida',
  complained: 'Reclamação',
};

const STATUS_TONE: Record<string, string> = {
  queued: 'bg-info/10 text-info border-info/40',
  sent: 'bg-info/10 text-info border-info/40',
  delivered: 'bg-good/10 text-good border-good/40',
  failed: 'bg-bad/10 text-bad border-bad/40',
  received: 'bg-good/10 text-good border-good/40',
  bounced: 'bg-bad/10 text-bad border-bad/40',
  complained: 'bg-warn/10 text-warn border-warn/40',
};

const CHANNEL_ICON: Record<string, JSX.Element> = {
  email: <Mail size={13} strokeWidth={1.8} />,
  whatsapp: <MessageCircle size={13} strokeWidth={1.8} />,
  sms: <MessageSquare size={13} strokeWidth={1.8} />,
};

export interface CommunicationsListProps {
  filters: CommunicationsFilters;
  /** Se true, oculta a coluna "Cliente" (já tá no contexto do detail). */
  hideClient?: boolean;
  /** Mensagem custom no empty state. */
  emptyLabel?: string;
}

export function CommunicationsList({
  filters,
  hideClient,
  emptyLabel,
}: CommunicationsListProps): JSX.Element {
  const list = useQuery({
    queryKey: ['communications', filters],
    queryFn: ({ signal }) => listCommunications(filters, signal),
    staleTime: 15_000,
  });

  if (list.isLoading) {
    return <p className="py-6 text-center text-[13px] text-muted">Carregando…</p>;
  }

  if (list.isError) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-[10px] border border-bad/40 bg-bad/5 py-6 text-[13px] text-bad">
        <AlertCircle size={14} strokeWidth={2} />
        Falha ao carregar comunicações.
      </div>
    );
  }

  const items = list.data?.data ?? [];
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-panel-2 text-muted">
          <Inbox size={18} strokeWidth={1.6} />
        </span>
        <p className="text-[13px] text-muted">{emptyLabel ?? 'Nenhuma comunicação registrada.'}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
          <tr>
            <th className="px-4 py-3">Canal</th>
            {!hideClient && <th className="px-4 py-3">Cliente</th>}
            <th className="px-4 py-3">Assunto / Template</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Direção</th>
            <th className="px-4 py-3">Quando</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((c: CommunicationItem) => (
            <tr key={c.id} className="hover:bg-panel-2">
              <td className="px-4 py-3 text-muted">
                <span className="inline-flex items-center gap-1 text-[12px]">
                  {CHANNEL_ICON[c.channel] ?? null}
                  {c.channel}
                </span>
              </td>
              {!hideClient && <td className="px-4 py-3 text-muted">{c.client?.name ?? '—'}</td>}
              <td className="max-w-[300px] truncate px-4 py-3">
                <div className="truncate text-ink">
                  {c.subject ?? c.templateKey ?? '(sem assunto)'}
                </div>
                <div className="truncate text-[11px] text-muted-2">{c.body}</div>
              </td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider',
                    STATUS_TONE[c.status] ?? 'border-line bg-panel-3 text-muted',
                  )}
                >
                  {STATUS_LABEL[c.status] ?? c.status}
                </span>
              </td>
              <td className="px-4 py-3 text-muted">
                {c.direction === 'inbound' ? '↓ recebida' : '↑ enviada'}
              </td>
              <td className="px-4 py-3 text-muted-2 tnum">
                {formatBRDateTime(c.sentAt ?? c.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
